import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { 
  Shield, 
  Search, 
  Download, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2,
  Eye,
  Users,
  AlertTriangle,
  Send,
  UserCheck,
  Lock,
  Copy,
  ExternalLink,
  Zap
} from 'lucide-react';
import { exportDataset } from '../../lib/exportData';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'En attente', class: 'bg-amber-100 text-amber-700', icon: Clock },
  validated: { label: 'Validee', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Refusee', class: 'bg-red-100 text-red-700', icon: XCircle },
};

const roleLabels = {
  rh: 'RH',
  commercial: 'AC',
};

export const AdminAttestations = () => {
  const [attestations, setAttestations] = useState([]);
  const [pendingAttestations, setPendingAttestations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAttestation, setSelectedAttestation] = useState(null);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [userHistory, setUserHistory] = useState([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // For requesting attestations
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isRequestOpen, setIsRequestOpen] = useState(false);
  
  // For auto-verification
  const [verifyingAttestationId, setVerifyingAttestationId] = useState(null);

  // Listen for messages from the URSSAF iframe
  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.data && event.data.type === 'urssaf-verification-result') {
        const result = event.data;
        console.log('URSSAF verification result:', result);
        
        if (result.valid === true) {
          toast.success('✅ Code valide ! Attestation authentique et valide');
          // Auto-validate if we have an attestation ID
          if (verifyingAttestationId) {
            await validateAttestation(verifyingAttestationId);
          }
        } else if (result.valid === false) {
          toast.error('❌ Code invalide - Attestation non valide', { duration: 5000 });
          // Suggest rejection
          if (verifyingAttestationId) {
            const att = pendingAttestations.find(a => a.attestation_id === verifyingAttestationId);
            if (att) {
              setSelectedAttestation(att);
              setRejectComment(defaultRejectComment);
              setIsRejectOpen(true);
            }
          }
        } else {
          toast.warning(result.message || 'Vérification manuelle requise');
        }
        setVerifyingAttestationId(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [verifyingAttestationId, pendingAttestations]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [allRes, pendingRes, usersRes] = await Promise.all([
        fetch(`${API}/admin/attestations`, { credentials: 'include' }),
        fetch(`${API}/admin/attestations/pending`, { credentials: 'include' }),
        fetch(`${API}/admin/attestations/users`, { credentials: 'include' }),
      ]);

      if (allRes.ok) setAttestations(await allRes.json());
      if (pendingRes.ok) setPendingAttestations(await pendingRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
    } catch (error) {
      console.error('Failed to fetch attestations:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateAttestation = async (attestationId) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/attestations/${attestationId}/validate`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Attestation validee - compte debloque');
        fetchData();
      } else {
        toast.error('Erreur lors de la validation');
      }
    } catch (error) {
      console.error('Validation error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const defaultRejectComment = `Ce numéro ne correspond à aucune attestation en cours de validité émise par l'Urssaf pour l'un des motifs suivants :

- Aucune attestation ne correspond à ce code sécurité.
- La date de validité de cette attestation est dépassée.
- Cette attestation a été modifiée de manière frauduleuse.`;

  const openRejectDialog = (attestation) => {
    setSelectedAttestation(attestation);
    setRejectComment(defaultRejectComment);
    setIsRejectOpen(true);
  };

  const handleReject = async () => {
    if (!rejectComment.trim()) {
      toast.error('Veuillez indiquer le motif du rejet');
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/attestations/${selectedAttestation.attestation_id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ comment: rejectComment }),
      });

      if (response.ok) {
        toast.success('Attestation refusee - compte bloque');
        setIsRejectOpen(false);
        fetchData();
      } else {
        toast.error('Erreur lors du rejet');
      }
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const autoVerifyAttestation = async (attestationId) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/attestations/${attestationId}/auto-verify`, {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();
      
      if (data.auto_validated) {
        toast.success('✅ Attestation validée automatiquement !');
        fetchData();
      } else if (data.suggest_reject) {
        toast.error('❌ Code invalide - Attestation non valide', { duration: 5000 });
        // Open reject dialog with pre-filled reason
        const att = pendingAttestations.find(a => a.attestation_id === attestationId);
        if (att) {
          setSelectedAttestation(att);
          setRejectComment(data.rejection_reason || defaultRejectComment);
          setIsRejectOpen(true);
        }
      } else {
        toast.warning(data.message || 'Vérification manuelle requise');
      }
    } catch (error) {
      console.error('Auto-verify error:', error);
      toast.error('Erreur lors de la vérification automatique');
    } finally {
      setActionLoading(false);
    }
  };

  const downloadAttestation = async (attestationId, filename) => {
    try {
      const response = await fetch(`${API}/admin/attestations/${attestationId}/download`, {
        credentials: 'include',
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'attestation.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Attestation telechargee');
      } else {
        toast.error('Erreur lors du telechargement');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du telechargement');
    }
  };

  const viewUserHistory = async (userId, userName) => {
    try {
      const response = await fetch(`${API}/admin/attestations/user/${userId}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const history = await response.json();
        setUserHistory(history);
        setSelectedUser({ userId, userName });
        setIsHistoryOpen(true);
      }
    } catch (error) {
      console.error('Failed to fetch user history:', error);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map(u => u.user_id));
    }
  };

  const requestAttestations = async (requestAll = false) => {
    if (!requestAll && selectedUserIds.length === 0) {
      toast.error('Selectionnez au moins un utilisateur');
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/attestations/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestAll ? { request_all: true } : { user_ids: selectedUserIds }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setSelectedUserIds([]);
        setIsRequestOpen(false);
        fetchData();
      } else {
        toast.error('Erreur lors de la demande');
      }
    } catch (error) {
      console.error('Request error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredPending = pendingAttestations.filter(att =>
    att.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    att.user_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredAll = attestations.filter(att =>
    att.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    att.user_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const validatedCount = attestations.filter(a => a.status === 'validated').length;
  const rejectedCount = attestations.filter(a => a.status === 'rejected').length;
  const blockedUsersCount = users.filter(u => u.attestation_required).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]" data-testid="admin-attestations-title">
            Attestations de vigilance
          </h1>
          <p className="text-slate-500 mt-1">Gerez les attestations URSSAF des AC et RH</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => exportDataset('attestations', 'demandes_AVU')} data-testid="export-attestations-btn">
            <Download className="w-4 h-4 mr-2" /> Exporter (CSV)
          </Button>
          <Button 
            variant="outline"
            onClick={() => window.open('https://www.urssaf.fr/accueil/outils-documentation/outils/verification-attestation.html', '_blank')}
            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Verifier une attestation
          </Button>
          <Button 
            onClick={() => setIsRequestOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <Send className="w-4 h-4 mr-2" />
            Demander des attestations
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-indigo-100">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Utilisateurs</p>
              <p className="text-xl font-bold text-slate-900">{users.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-red-100">
              <Lock className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Bloques</p>
              <p className="text-xl font-bold text-red-600">{blockedUsersCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">En attente</p>
              <p className="text-xl font-bold text-amber-600">{pendingAttestations.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Validees</p>
              <p className="text-xl font-bold text-emerald-600">{validatedCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-slate-100">
              <Shield className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-xl font-bold text-slate-900">{attestations.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Rechercher par nom ou email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-attestations"
        />
      </div>

      {/* Pending Alert */}
      {pendingAttestations.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-500 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="text-amber-800">
              <strong>{pendingAttestations.length}</strong> attestation(s) en attente de validation
            </span>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            En attente ({pendingAttestations.length})
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Utilisateurs ({users.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Historique ({attestations.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending Tab */}
        <TabsContent value="pending">
          <Card className="border-0 shadow-sm">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Utilisateur</TableHead>
                    <TableHead className="font-semibold">Role</TableHead>
                    <TableHead className="font-semibold">Document</TableHead>
                    <TableHead className="font-semibold">Code URSSAF</TableHead>
                    <TableHead className="font-semibold">Date depot</TableHead>
                    <TableHead className="font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        Chargement...
                      </TableCell>
                    </TableRow>
                  ) : filteredPending.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        Aucune attestation en attente
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPending.map((att) => (
                      <TableRow key={att.attestation_id} className="hover:bg-slate-50">
                        <TableCell>
                          <div>
                            <p className="font-medium">{att.user_name}</p>
                            <p className="text-sm text-slate-500">{att.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            att.user_role === 'rh' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'
                          }>
                            {roleLabels[att.user_role] || att.user_role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-600">{att.filename}</TableCell>
                        <TableCell>
                          {att.security_code ? (
                            <div className="flex items-center gap-2">
                              <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono text-slate-700">
                                {att.security_code}
                              </code>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => {
                                  navigator.clipboard.writeText(att.security_code);
                                  toast.success('Code copie !');
                                }}
                                title="Copier le code"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {new Date(att.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadAttestation(att.attestation_id, att.filename)}
                              title="Télécharger"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {att.security_code && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  const iframe = document.getElementById('urssaf-iframe');
                                  if (iframe) {
                                    iframe.src = `${API}/proxy/urssaf-verification?code=${att.security_code}`;
                                    // Scroll to iframe
                                    setTimeout(() => {
                                      iframe.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 500);
                                  }
                                  toast.success(`Code ${att.security_code} injecté - cliquez sur "Vérifier une attestation" dans l'iframe`);
                                }}
                                className="bg-amber-500 hover:bg-amber-600 text-white"
                                title="Injecter le code dans l'iframe URSSAF"
                              >
                                <Zap className="w-4 h-4 mr-1" />
                                Vérifier
                              </Button>
                            )}
                            <Button
                              size="sm"
                              onClick={() => validateAttestation(att.attestation_id)}
                              disabled={actionLoading}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Valider
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openRejectDialog(att)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Refuser
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* URSSAF Verification Iframe via Backend Proxy */}
          {filteredPending.length > 0 && (
            <Card className="border-0 shadow-sm mt-6">
              <CardHeader className="pb-2 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-100">
                      <Shield className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Verification URSSAF en ligne</CardTitle>
                      <p className="text-sm text-slate-500">
                        Cliquez sur "Verifier" dans le tableau, puis sur "Verifier une attestation" ci-dessous
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open('https://www.urssaf.fr/accueil/outils-documentation/outils/verification-attestation.html', '_blank')}
                    className="text-slate-600 border-slate-300 hover:bg-slate-50"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Nouvel onglet
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <iframe
                  id="urssaf-iframe"
                  src={`${API}/proxy/urssaf-verification`}
                  title="Verification attestation URSSAF"
                  className="w-full h-[700px] border-0 rounded-b-lg"
                  style={{ marginTop: '-1px' }}
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="border-0 shadow-sm">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Utilisateur</TableHead>
                    <TableHead className="font-semibold">Role</TableHead>
                    <TableHead className="font-semibold">Statut compte</TableHead>
                    <TableHead className="font-semibold">Derniere attestation</TableHead>
                    <TableHead className="font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        Chargement...
                      </TableCell>
                    </TableRow>
                  ) : filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        Aucun utilisateur
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((u) => (
                      <TableRow key={u.user_id} className={`hover:bg-slate-50 ${u.attestation_required ? 'bg-red-50' : ''}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{u.name}</p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            u.role === 'rh' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'
                          }>
                            {roleLabels[u.role] || u.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {u.attestation_required ? (
                            <Badge className="bg-red-100 text-red-700 border border-red-300">
                              <Lock className="w-3 h-3 mr-1" />
                              Bloque
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300">
                              <UserCheck className="w-3 h-3 mr-1" />
                              Actif
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {u.latest_attestation ? (
                            <div>
                              <Badge className={`${statusConfig[u.latest_attestation.status]?.class} border text-xs`}>
                                {statusConfig[u.latest_attestation.status]?.label}
                              </Badge>
                              <p className="text-xs text-slate-500 mt-1">
                                {new Date(u.latest_attestation.created_at).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                          ) : (
                            <span className="text-slate-400">Aucune</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewUserHistory(u.user_id, u.name)}
                              className="text-indigo-600"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedUserIds([u.user_id]);
                                requestAttestations(false);
                              }}
                              disabled={actionLoading}
                              className={u.attestation_required 
                                ? "text-red-600 border-red-300 hover:bg-red-50"
                                : "text-amber-600 border-amber-300 hover:bg-amber-50"
                              }
                            >
                              <Send className="w-4 h-4 mr-1" />
                              {u.attestation_required ? 'Relancer' : 'Demander'}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card className="border-0 shadow-sm">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Utilisateur</TableHead>
                    <TableHead className="font-semibold">Role</TableHead>
                    <TableHead className="font-semibold">Document</TableHead>
                    <TableHead className="font-semibold">Statut</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        Chargement...
                      </TableCell>
                    </TableRow>
                  ) : filteredAll.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        Aucune attestation
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAll.map((att) => {
                      const StatusIcon = statusConfig[att.status]?.icon || Clock;
                      return (
                        <TableRow key={att.attestation_id} className="hover:bg-slate-50">
                          <TableCell>
                            <div>
                              <p className="font-medium">{att.user_name}</p>
                              <p className="text-sm text-slate-500">{att.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              att.user_role === 'rh' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'
                            }>
                              {roleLabels[att.user_role] || att.user_role}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-600">{att.filename}</TableCell>
                          <TableCell>
                            <Badge className={`${statusConfig[att.status]?.class} border`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig[att.status]?.label}
                            </Badge>
                            {att.rejection_comment && (
                              <p className="text-xs text-red-600 mt-1 max-w-[150px] truncate" title={att.rejection_comment}>
                                {att.rejection_comment}
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {new Date(att.created_at).toLocaleDateString('fr-FR')}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadAttestation(att.attestation_id, att.filename)}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => viewUserHistory(att.user_id, att.user_name)}
                                className="text-indigo-600"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Request Attestations Dialog */}
      <Dialog open={isRequestOpen} onOpenChange={setIsRequestOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-indigo-600" />
              Demander des attestations
            </DialogTitle>
            <DialogDescription>
              Selectionnez les utilisateurs qui doivent deposer une attestation de vigilance
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllUsers}
              >
                {selectedUserIds.length === users.length 
                  ? 'Deselectionner tout' 
                  : 'Selectionner tout'
                }
              </Button>
              <span className="text-sm text-slate-500">
                {selectedUserIds.length} selectionne(s)
              </span>
            </div>
            
            <div className="max-h-[300px] overflow-y-auto border rounded-lg">
              {users.length === 0 ? (
                <p className="text-center py-8 text-slate-500">
                  Aucun utilisateur disponible
                </p>
              ) : (
                users.map((u) => (
                  <div 
                    key={u.user_id}
                    className={`flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-slate-50 cursor-pointer ${
                      selectedUserIds.includes(u.user_id) ? 'bg-indigo-50' : ''
                    } ${u.attestation_required ? 'bg-red-50' : ''}`}
                    onClick={() => toggleUserSelection(u.user_id)}
                  >
                    <Checkbox
                      checked={selectedUserIds.includes(u.user_id)}
                      onCheckedChange={() => toggleUserSelection(u.user_id)}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{u.name}</p>
                      <p className="text-sm text-slate-500">{u.email}</p>
                    </div>
                    {u.attestation_required && (
                      <Badge className="bg-red-100 text-red-700 border border-red-300 mr-2">
                        <Lock className="w-3 h-3 mr-1" />
                        Bloque
                      </Badge>
                    )}
                    <Badge variant="outline" className={
                      u.role === 'rh' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'
                    }>
                      {roleLabels[u.role]}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsRequestOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="outline"
              onClick={() => requestAttestations(true)}
              disabled={actionLoading}
              className="text-amber-600 border-amber-300 hover:bg-amber-50"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
              Demander a tous
            </Button>
            <Button
              onClick={() => requestAttestations(false)}
              disabled={actionLoading || selectedUserIds.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Demander aux selectionnes ({selectedUserIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={isRejectOpen} onOpenChange={setIsRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refuser l'attestation</DialogTitle>
            <DialogDescription>
              Indiquez le motif du rejet pour {selectedAttestation?.user_name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Motif du rejet (ex: document illisible, date expiree...)"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRejectOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Refuser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User History Dialog */}
      <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Historique - {selectedUser?.userName}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4 text-slate-500">
                      Aucun historique
                    </TableCell>
                  </TableRow>
                ) : (
                  userHistory.map((att) => {
                    const StatusIcon = statusConfig[att.status]?.icon || Clock;
                    return (
                      <TableRow key={att.attestation_id}>
                        <TableCell>{att.filename}</TableCell>
                        <TableCell className="text-slate-500">
                          {new Date(att.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusConfig[att.status]?.class} border`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig[att.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadAttestation(att.attestation_id, att.filename)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
